import { type ReactNode, useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { BotaoAdicionar } from "./BotaoAdicionar";
import { AdicionarDaBaseDialog } from "@/components/catalogo/base/AdicionarDaBaseDialog";
import { CatalogItemRow } from "./CatalogItemRow";
import { ShadeOptionDialog } from "./ShadeOptionDialog";
import { type Shade, type Subgroup, groupBySystem, groupsGridCls, splitInHalf, systemLabel } from "./shade-groups";

const cardCls = "rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card";
const sectionTitleCls = "text-sm font-semibold text-zinc-900 dark:text-foreground";
const groupLabelCls = "text-xs font-semibold text-zinc-700 dark:text-foreground/80";
const subgroupLabelCls = "mt-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-muted-foreground/70";

/** Cada sistema (VITA Classical, 3D-Master...) mostra seus subgrupos (A/B/C/D, 1-5...) em 2 colunas
 *  — os códigos são siglas curtas, uma coluna só sobra muito espaço vazio. */
function SystemGroupCard({
  system,
  subgroups,
  renderItem,
  header,
  aberto,
  onAlternar,
}: {
  system: string;
  subgroups: Subgroup[];
  renderItem: (shade: Shade) => ReactNode;
  /** Substitui o título por um controle — usado pelo checkbox que liga/desliga a escala inteira. */
  header?: ReactNode;
  /** Fechado por padrão: são dezenas de códigos por escala, e o dia a dia é ligar/desligar a
   *  escala inteira — ver código por código é a exceção. */
  aberto: boolean;
  onAlternar: () => void;
}) {
  const showSubgroupLabel = subgroups.length > 1;
  const [col1, col2] = showSubgroupLabel ? splitInHalf(subgroups) : [subgroups, []];

  const renderColumn = (col: Subgroup[]) => (
    <div>
      {col.map(([key, items]) => (
        <div key={key}>
          {showSubgroupLabel && <p className={subgroupLabelCls}>{key}</p>}
          {items.map(renderItem)}
        </div>
      ))}
    </div>
  );

  return (
    <div className="rounded-lg border border-zinc-100 p-3 dark:border-border/60">
      <div className="flex items-center gap-1">
        <div className="min-w-0 flex-1">{header ?? <p className={groupLabelCls}>{systemLabel(system)}</p>}</div>
        <button
          type="button"
          onClick={onAlternar}
          aria-expanded={aberto}
          aria-label={`${aberto ? "Ocultar" : "Ver"} cores de ${systemLabel(system)}`}
          title={aberto ? "Ocultar cores" : "Ver cores"}
          className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] font-semibold text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-800 dark:text-muted-foreground dark:hover:bg-white/5 dark:hover:text-foreground"
        >
          {aberto ? "Ocultar" : "Ver cores"}
          <ChevronDown className={cn("h-3.5 w-3.5 transition-transform", aberto && "rotate-180")} />
        </button>
      </div>
      {aberto &&
        (col2.length > 0 ? (
          <div className="mt-1 grid grid-cols-2 gap-x-4">
            {renderColumn(col1)}
            {renderColumn(col2)}
          </div>
        ) : (
          <div className="mt-1">{renderColumn(col1)}</div>
        ))}
    </div>
  );
}

export function ShadeOptionSection() {
  const utils = trpc.useUtils();
  const listQuery = trpc.prosthesis.catalog.myShadeOptions.useQuery();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [daBase, setDaBase] = useState(false);

  const invalidar = () => {
    utils.prosthesis.catalog.myShadeOptions.invalidate();
    utils.prosthesis.catalog.shadeOptions.invalidate();
  };
  const toggleSystem = trpc.prosthesis.catalog.setShadeSystemEnabled.useMutation({
    onSuccess: invalidar,
    onError: (e) => toast.error(e.message || "Erro ao atualizar escala."),
  });
  const toggle = trpc.prosthesis.catalog.setShadeOptionEnabled.useMutation({
    onSuccess: invalidar,
    onError: (e) => toast.error(e.message || "Erro ao atualizar cor."),
  });
  const remove = trpc.prosthesis.catalog.deleteCustomShadeOption.useMutation({
    onSuccess: () => {
      toast.success("Cor excluída.");
      utils.prosthesis.catalog.myShadeOptions.invalidate();
      utils.prosthesis.catalog.shadeOptions.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao excluir cor."),
  });

  const grupos = groupBySystem((listQuery.data ?? []) as Shade[]);
  const [abertas, setAbertas] = useState<Set<string>>(new Set());
  const alternar = (system: string) =>
    setAbertas((prev) => {
      const next = new Set(prev);
      if (next.has(system)) next.delete(system);
      else next.add(system);
      return next;
    });

  return (
    <div id="cores" className={cardCls}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 data-tour="catalogo-gerenciar-secao-titulo" className={sectionTitleCls}>Cores</h2>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            As escalas do seu laboratório — só as ativas aparecem pros dentistas ao montar um pedido.
          </p>
        </div>
        <BotaoAdicionar topo onClick={() => setDaBase(true)}>
          Adicionar cores
        </BotaoAdicionar>
        <AdicionarDaBaseDialog tipo="cores" open={daBase} onClose={() => setDaBase(false)} onCriarNovo={() => setDialogOpen(true)} />
      </div>

      <div className="mt-3">
        {grupos.length === 0 ? (
          <p className="px-2 py-1.5 text-xs text-zinc-400 dark:text-muted-foreground">Nenhuma cor no catálogo ainda.</p>
        ) : (
          <div className={`grid gap-4 ${groupsGridCls(grupos.length)}`}>
            {grupos.map((g) => {
              const ativas = g.items.filter((s) => s.active).length;
              // O checkbox do cabeçalho liga/desliga a escala inteira; indeterminado quando só
              // parte dela está ativa (dá pra desativar cor a cor pelo checkbox de cada linha).
              const agregado: boolean | "indeterminate" =
                ativas === 0 ? false : ativas === g.items.length ? true : "indeterminate";
              return (
                <SystemGroupCard
                  key={g.system}
                  system={g.system}
                  subgroups={g.subgroups}
                  aberto={abertas.has(g.system)}
                  onAlternar={() => alternar(g.system)}
                  header={
                    <div className="min-w-0">
                      <CatalogItemRow
                        label={systemLabel(g.system)}
                        sublabel={`${ativas} de ${g.items.length} ativas`}
                        enabled={agregado}
                        disabled={toggleSystem.isPending}
                        onToggle={(enabled) => toggleSystem.mutate({ system: g.system, enabled })}
                      />
                    </div>
                  }
                  renderItem={(s) => (
                    <CatalogItemRow
                      key={s.id}
                      label={s.code}
                      proprio={s.originGlobalId === null && !s.origemNaBaseId}
                      enabled={s.active}
                      disabled={toggle.isPending || remove.isPending}
                      onToggle={(enabled) => toggle.mutate({ shadeOptionId: s.id, enabled })}
                      onDelete={() => {
                        if (window.confirm(`Excluir a cor "${s.code}"?`)) remove.mutate({ shadeOptionId: s.id });
                      }}
                    />
                  )}
                />
              );
            })}
          </div>
        )}
      </div>

      <ShadeOptionDialog open={dialogOpen} onClose={() => setDialogOpen(false)} />
    </div>
  );
}
